// src/hooks/media/useSingleImageField.js
import { useCallback, useEffect, useState } from "react";

export default function useSingleImageField({
  initialImage = null, // { id, url, file_name } أو { id, url, name } أو null
  uploadMedia,         // mutate من useUploadMedia
  isArabic = true,
}) {
  const [imageId, setImageId] = useState(initialImage?.id ?? null);
  const [imageName, setImageName] = useState(
    initialImage?.file_name || initialImage?.name || ""
  );
  const [imagePreview, setImagePreview] = useState(
    initialImage?.url || initialImage?.preview || ""
  );
  const [imageError, setImageError] = useState("");
  const [imageRemoved, setImageRemoved] = useState(false);

  // تنظيف URL.createObjectURL لتجنب memory leak
  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  // تستخدمها في edit لما يوصلك currentData
  const setFromServerImage = useCallback((img) => {
    setImageId(img?.id ?? null);
    setImageName(img?.file_name || img?.name || "");
    setImagePreview(img?.url || img?.preview || "");
    setImageError("");
    setImageRemoved(false);
  }, []);

  const handleImageChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];

      setImageError("");
      setImageRemoved(false);

      if (!file) return;

      // أول ما يختار صورة جديدة: اعرض preview محلي + صفّر id القديمة
      const localPreviewUrl = URL.createObjectURL(file);
      setImagePreview(localPreviewUrl);
      setImageId(null);
      setImageName("");

      const fd = new FormData();
      fd.append("file", file);

      uploadMedia(fd, {
        onSuccess: (res) => {
          const fileName = res?.data?.file_name || res?.data?.name || "";
          if (!fileName) {
            setImageError(
              isArabic
                ? "لا يمكن الحصول على اسم الملف من الخادم"
                : "Cannot get uploaded file name from server"
            );
            return;
          }

          // الرفع عندك لا يرجّع id
          setImageName(fileName);
        },
        onError: () => {
          setImageError(
            isArabic
              ? "فشل رفع الصورة، الرجاء المحاولة مرة أخرى"
              : "Failed to upload image, please try again"
          );
        },
      });
    },
    [uploadMedia, isArabic]
  );

  // تتنادى بعد ما InputFileUpload يخلص حذف (سيرفر/محلي)
  const handleDeleted = useCallback(() => {
    setImageId(null);
    setImageName("");
    setImagePreview("");
    setImageRemoved(true);
    setImageError("");
  }, []);

  // ✅ أهم نقطة: لا ترسل key image نهائيًا لو مفيش imageName
  const getImagePayload = useCallback(() => {
    return imageName ? { image: imageName } : {};
  }, [imageName]);

  return {
    imageId,
    imageName,
    imagePreview,
    imageError,
    imageRemoved,

    setFromServerImage,
    setImageRemoved,

    handleImageChange,
    handleDeleted,

    getImagePayload,
  };
}
